import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Typography,
  List,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import placeholderThumb from 'styles/assets/placeholder/placeholderThumbTriibo.webp';
import { downloadImage } from 'js/library/services/StorageManager.js';
import { recommendOrgs } from 'js/library/utils/API/recommendOrgs.js';
import { trackEventMatomoElementId, trackEventMatomo } from 'js/library/utils/helpers';
import { useCurrentUserInfo } from 'js/library/services/currentUserInfo';

const verifyLocalization = false;

export const HomeTriibosSuggestion = ({ orgs: shouldReload }) => {
  const [orgs, setOrgs] = React.useState(false);
  const loadedUserKeyRef = React.useRef(null);
  const requestIdRef = React.useRef(0);
  let timeDebounce = null;

  const { t } = useTranslation('app');

  const { userInfo, loading } = useCurrentUserInfo();
  const userKey = userInfo?.uId || userInfo?.guestId || userInfo?.triiboId || null;

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (loading || !userKey) {
      return undefined;
    }

    if (!shouldReload && loadedUserKeyRef.current === userKey) {
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setOrgs(false);

    (async function fetchRecommendedOrgs() {
      try {
        const result = await recommendOrgs(userInfo?.uId);
        if (requestId !== requestIdRef.current) return;

        loadedUserKeyRef.current = userKey;
        setOrgs(result);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;

        setOrgs(null);
        console.error('Error fetching recommended organizations:', error);
      }
    })();
  }, [loading, shouldReload, userInfo?.uId, userKey]);

  function downloadImageOnLoad(i) {
    if (!orgs || !Array.isArray(orgs) || !orgs[i]) return;
    if (orgs[i].downloaded !== undefined) return;

    const thumbnail = orgs[i].thumbnail;

    if (!thumbnail || typeof thumbnail !== 'string') {
      return;
    }

    const img = thumbnail.split('/');

    if (!img || img.length < 2) {
      return;
    }

    downloadImage(img[0], img[1])
      .then((downloadedImg) => {
        if (orgs && orgs[i]) {
          const array = [...orgs];
          array[i].downloaded = downloadedImg ?? placeholderThumb;
          setOrgs(array);
        }
      })
      .catch(() => {
        if (orgs && orgs[i]) {
          const array = [...orgs];
          array[i].downloaded = placeholderThumb;
          setOrgs(array);
        }
      });
  }

  function scrollMatomo() {
    clearTimeout(timeDebounce);
    timeDebounce = setTimeout(() => {
      trackEventMatomo('SugeridosParaVoce', 'scroll', 'lista', 'sugeridos para você');
    }, 400);
  }

  return (
    <>
      {verifyLocalization ? null : (
        <div className="home-paper-triibos">
          <Typography
            variant="body1"
            className="home-paper-triibos-title"
            style={{ color: '#6e3296', fontWeight: 600 }}
          >
            {t('home.suggestions_to_you')}
          </Typography>

          <div onScroll={() => scrollMatomo()} className="home-paper-list">
            <List>
              {orgs === false || shouldReload ? (
                <div style={{ textAlign: 'center' }}>
                  <CircularProgress color="primary" />
                </div>
              ) : orgs === null || orgs.length === 0 ? (
                <div style={{ padding: '0px 5px' }}>
                  <Typography variant="subtitle2" color="secondary">
                    {t('home.dont_have_any_recommendations')}
                  </Typography>
                </div>
              ) : (
                orgs.map((triibo, i) => {
                  return (
                    <Link
                      to={{
                        pathname: '/triibos/',
                        search: '?id=' + triibo.orgID,
                        state: triibo,
                      }}
                      style={{ textDecoration: 'none' }}
                      onClick={() => {
                        trackEventMatomoElementId(
                          'SugeridosParaVoce',
                          'tocar',
                          'sugeridos',
                          triibo.orgID
                        );
                      }}
                    >
                      <ListItem key={triibo.orgID}>
                        <ListItemAvatar>
                          <Avatar>
                            <img
                              src={
                                triibo.downloaded === undefined
                                  ? placeholderThumb
                                  : triibo.downloaded
                              }
                              width="100%"
                              alt="thumb"
                              onLoad={() => downloadImageOnLoad(i)}
                            />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText style={{ color: 'black' }} primary={triibo.name} />
                      </ListItem>
                    </Link>
                  );
                })
              )}
            </List>
          </div>
        </div>
      )}
    </>
  );
};

export default HomeTriibosSuggestion;
